import { addDays, format } from "date-fns";
import { fr } from "date-fns/locale";
import { Phone, PhoneOff, SearchX } from "lucide-react";
import {
  type Identifier,
  ListBase,
  useCreate,
  useGetIdentity,
  useGetList,
  useListContext,
  useUpdate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { toast } from "sonner";

import { ListPagination } from "@/components/admin/list-pagination";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { TemperatureBadge } from "../contacts/ContactBadges";
import { HeatTierBadge } from "../contacts/ContactListContent";
import { RecordButton } from "../recordings/RecordButton";
import { EmptyState } from "../shared/EmptyState";
import type { Tag, Temperature } from "../types";

// Reprise 07/09/2026 — File d'appel partagée (route /appels).
//
// Source : vue `call_queue` (03_views.sql). Elle fait DÉJÀ tout le filtrage
// (population, téléphone présent, non perdu, échéance, cooldown 7 j) : ici on
// ne refiltre rien côté client, on pagine et on trie côté serveur — c'est le
// modèle de MyDayActionsList, pas celui de MyDayContactsToRelance.
//
// Le tri est TOUJOURS queue_rank ASC : ra-supabase émet systématiquement son
// propre `order=` (providers/supabase/dataProvider.ts:29) qui écrase l'ORDER BY
// de la vue. queue_rank est un row_number() qui encode le tri à 6 clés.
//
// La file est partagée (décision fondateur) : aucun filtre sales_id.

const ALL_CAMPAIGNS = "__all__";

export type CallQueueContact = {
  id: Identifier;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  company_id: number | null;
  company_name: string | null;
  company_city: string | null;
  company_sector: string | null;
  company_phone: string | null;
  phone_number: string | null;
  temperature: Temperature | null;
  heat_score: number | null;
  heat_tier: string | null;
  next_action_date: string | null;
  background: string | null;
  queue_rank: number;
};

const displayName = (row: CallQueueContact) =>
  `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "Sans nom";

export const CallQueuePage = () => (
  <ListBase<CallQueueContact>
    resource="call_queue"
    perPage={25}
    sort={{ field: "queue_rank", order: "ASC" }}
  >
    <CallQueueLayout />
  </ListBase>
);

CallQueuePage.path = "/appels";

const CallQueueLayout = () => {
  const { data, error, isPending, total } = useListContext<CallQueueContact>();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 bg-card border border-border rounded-xl">
        <div className="flex items-center gap-2 shrink-0">
          <Phone className="size-4 text-fabrik-accent-text" />
          <h1 className="text-sm font-semibold">File d'appel</h1>
        </div>
        <CampaignFilter />
        <span className="ml-auto text-[12.5px] text-muted-foreground tabular-nums shrink-0">
          {isPending
            ? "Chargement…"
            : `${total ?? 0} prospect${(total ?? 0) > 1 ? "s" : ""} à appeler`}
        </span>
      </div>

      <Card className="py-0">
        {isPending ? (
          <div className="p-3 flex flex-col gap-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-muted-foreground">
            Impossible de charger la file d'appel.
          </div>
        ) : !data?.length ? (
          <EmptyState
            icon={<SearchX className="size-6" />}
            title="Aucun prospect à appeler"
            description="La file est vide pour cette campagne : tout le monde est en cooldown, rappelé plus tard, ou marqué perdu."
          />
        ) : (
          data.map((row) => <CallQueueRow key={row.id} row={row} />)
        )}
      </Card>

      {/* Conditionné sur le total, pas sur la page : quand la page courante se
          vide (dernier prospect traité), la pagination doit rester affichée
          pour permettre de revenir en arrière. */}
      {!isPending && !!total && <ListPagination />}
    </div>
  );
};

const CampaignFilter = () => {
  const { filterValues, setFilters } = useListContext<CallQueueContact>();
  // La campagne = les tags (surete-2026-08, incendie-2026-08, …).
  const { data: tags } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const active = (filterValues?.["tags@cs"] as string | undefined) ?? null;

  const handleChange = (value: string) => {
    const next = { ...(filterValues ?? {}) };
    if (value === ALL_CAMPAIGNS) {
      delete next["tags@cs"];
    } else {
      next["tags@cs"] = value;
    }
    setFilters?.(next, []);
  };

  return (
    <Select value={active ?? ALL_CAMPAIGNS} onValueChange={handleChange}>
      <SelectTrigger
        className="h-8 w-[240px] text-[13px]"
        aria-label="Filtrer par campagne"
      >
        <SelectValue placeholder="Toutes les campagnes" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_CAMPAIGNS}>Toutes les campagnes</SelectItem>
        {tags?.map((tag) => (
          <SelectItem key={tag.id} value={`{${tag.id}}`}>
            {tag.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const CallQueueRow = ({ row }: { row: CallQueueContact }) => {
  const name = displayName(row);
  // company_phone est le repli quand le contact n'a qu'un standard.
  const phone = row.phone_number ?? row.company_phone;
  const location = [row.company_city, row.company_sector]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="border-b border-border/50 last:border-b-0 px-3 py-3 hover:bg-muted/40 transition-colors">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-[200px]">
          <Link
            to={`/contacts/${row.id}/show`}
            className="text-sm font-medium hover:underline"
          >
            {name}
          </Link>
          {row.title && (
            <div className="text-xs text-muted-foreground truncate">
              {row.title}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 basis-[200px]">
          {row.company_id != null ? (
            <Link
              to={`/companies/${row.company_id}/show`}
              className="block truncate text-sm hover:underline"
            >
              {row.company_name ?? "—"}
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
          {location && (
            <div className="text-xs text-muted-foreground truncate">
              {location}
            </div>
          )}
        </div>

        <div className="basis-[160px] shrink-0">
          {phone ? (
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-fabrik-blue hover:underline tabular-nums"
            >
              <Phone className="size-3.5 shrink-0" />
              {phone}
            </a>
          ) : (
            <span className="text-xs text-muted-foreground">Pas de numéro</span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <TemperatureBadge value={row.temperature} />
          <HeatTierBadge tier={row.heat_tier} score={row.heat_score} />
        </div>

        <div className="ml-auto flex items-center gap-1 shrink-0">
          {/* Le point du chantier : enregistrer sans ouvrir la fiche. */}
          <RecordButton contactId={Number(row.id)} contactName={name} />
          <NoAnswerButton id={row.id} name={name} />
        </div>
      </div>

      {(row.next_action_date || row.background) && (
        <p
          className="mt-1.5 text-xs text-muted-foreground line-clamp-2"
          title={row.background ?? undefined}
        >
          {row.next_action_date && (
            <span className="font-medium text-foreground/80">
              Rappel promis le{" "}
              {format(new Date(row.next_action_date), "d MMM", { locale: fr })}
              {row.background ? " · " : ""}
            </span>
          )}
          {row.background}
        </p>
      )}
    </div>
  );
};

/**
 * ~40 % des appels à froid ne produisent aucun enregistrement, donc aucun
 * déclenchement de trg_apply_call_outcome : sans ce bouton la file ne se vide
 * que sur les appels décrochés.
 *
 * On écrit UNIQUEMENT `next_action_date` (J+2), volontairement PAS
 * `last_relance_at`. La vue `call_queue` exclut déjà toute ligne dont
 * `next_action_date > current_date` — vérifié en prod le 07/09/2026 : 20
 * contacts en sont exclus par ce seul critère. Écrire `last_relance_at`
 * déclencherait en plus le cooldown de 7 jours de la vue, qui écraserait
 * complètement l'intention « je retente dans 2 jours » : le contact ne
 * reviendrait qu'à J+7.
 *
 * Sémantiquement c'est aussi le bon découpage : `last_relance_at` veut dire
 * « on a effectivement touché quelqu'un », et c'est le trigger
 * `trg_apply_call_outcome` qui le pose après un appel réellement décroché et
 * transcrit. Un numéro qui sonne dans le vide n'est pas une relance.
 *
 * `next_action_type = 'a_appeler'` est posé pour que le rappel remonte aussi
 * dans « Mon jour » (MyDayActionsList filtre sur next_action_type@neq=aucune,
 * ce qui exclut les valeurs nulles).
 *
 * Une note est écrite à chaque tentative : sans elle, un contact appelé vingt
 * fois sans succès aurait exactement le même historique qu'un contact jamais
 * composé. La note est la seule trace d'un appel sans décroché, puisque aucun
 * `contact_recordings` n'est créé. Elle sert aussi de compteur de tentatives —
 * il suffit de compter les notes commençant par « Appel sans réponse ».
 * `sales_id` est posé explicitement depuis l'identité (même motif que
 * MyDayContactsToRelance:212) plutôt que laissé au trigger
 * `set_sales_id_default`, pour ne pas dépendre du contexte d'authentification.
 */
const NoAnswerButton = ({ id, name }: { id: Identifier; name: string }) => {
  const [update, { isPending }] = useUpdate();
  const [createNote] = useCreate();
  const { identity } = useGetIdentity();
  const queryClient = useQueryClient();

  const handleClick = () => {
    const now = new Date();
    update(
      "contacts",
      {
        id,
        data: {
          next_action_date: format(addDays(now, 2), "yyyy-MM-dd"),
          next_action_type: "a_appeler",
        },
      },
      {
        onSuccess: () => {
          toast.success(
            `Pas de réponse — ${name} revient dans la file dans 2 jours`,
          );
          queryClient.invalidateQueries({ queryKey: ["call_queue"] });

          // Best-effort : la trace ne doit pas faire échouer la sortie de file.
          // Si elle échoue, le contact est quand même reprogrammé — on prévient
          // sans bloquer le commercial, qui enchaîne les appels.
          createNote(
            "contact_notes",
            {
              data: {
                contact_id: Number(id),
                text: `Appel sans réponse — rappel programmé au ${format(
                  addDays(now, 2),
                  "d MMMM yyyy",
                  { locale: fr },
                )}.`,
                // `no_answer` n'est pas décoratif : compute_contact_status
                // exclut ces notes de son scoring. Sans ce statut, une seule
                // non-réponse vaudrait 8 points (7j×5 + 30j×2 + total×1) et
                // ferait passer le contact de cold à warm ; deux le rendraient
                // « hot ». Cf. migration exclude_no_answer_notes_from_contact_status.
                status: "no_answer",
                sales_id: identity?.id,
                date: now.toISOString(),
              },
            },
            {
              onSuccess: () =>
                queryClient.invalidateQueries({ queryKey: ["contact_notes"] }),
              onError: () =>
                toast.warning(
                  "Contact reprogrammé, mais la trace n'a pas pu être écrite",
                ),
            },
          );
        },
        onError: () => toast.error("Échec de l'enregistrement"),
      },
    );
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={handleClick}
          disabled={isPending}
          aria-label={`Pas de réponse pour ${name}`}
        >
          <PhoneOff className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Pas de réponse</TooltipContent>
    </Tooltip>
  );
};
